import React, { useMemo, useState } from "react";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  LabelList,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import styled from "styled-components";
import { colors } from "utils";
import { BAR_SEGMENTS, KEY_LEVELS } from "components/pages/Educator/StudentReports/proficiencyStandards";

const Wrapper = styled.div`
  background: ${colors.white};
  border: 1px solid ${colors.nightlyWoods200 || "#E5E7EB"};
  border-radius: 8px;
  margin-bottom: 16px;
  padding: 16px;
`;

const Header = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
`;

const Title = styled.div`
  color: ${colors.nightlyWoods900 || "#111827"};
  flex: 1;
  font-size: 14px;
  font-weight: 600;
  text-align: center;
`;

const Toggle = styled.div`
  background: ${colors.milkGlass500};
  border: 1px solid ${colors.nightlyWoods100};
  border-radius: 6px;
  display: inline-flex;
  overflow: hidden;
  padding: 2px;
`;

const ToggleButton = styled.button`
  background: ${({ $active }) => ($active ? colors.nightlyWoods100 : "transparent")};
  border: none;
  border-radius: 4px;
  color: ${({ $active }) => ($active ? colors.nightlyWoods900 : colors.milkGlass950)};
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  padding: 4px 12px;
  transition: background 0.15s ease, color 0.15s ease;

  &:focus {
    outline: none;
  }

  &:focus-visible {
    box-shadow: 0 0 0 2px ${colors.blue300};
  }

  &:hover:not(:disabled) {
    color: ${({ $active }) => ($active ? colors.white : colors.nightlyWoods700)};
  }
`;

const Empty = styled.div`
  color: ${colors.nightlyWoods700 || "#6B7280"};
  font-size: 13px;
  padding: 16px;
  text-align: center;
`;

// A function, not a constant: proficiencyStandards -> studentReportTable -> utils
// -> generateRoutePath -> config/routes -> CSReports -> here is an import cycle,
// so KEY_LEVELS is still undefined at module load whenever proficiencyStandards
// is imported first (its own tests do).
const levelDefs = () =>
  KEY_LEVELS.map(({ level, label }) => ({
    level,
    label: `Level ${level}: ${label.replace(/\n/g, " ")}`,
    color: BAR_SEGMENTS.find((s) => s.level === level).fill,
  }));

const UNCLASSIFIED_DEF = {
  key: "unclassified",
  label: "Unclassified",
  color: "#9ca3af",
};

const MONTH_LABELS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

const pad2 = (n) => String(n).padStart(2, "0");

const monthBucket = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return null;
  const key = `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}`;
  const label = `${MONTH_LABELS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
  return { key, label };
};

const weekBucket = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return null;
  const day = d.getUTCDay();
  const diffToMonday = day === 0 ? -6 : 1 - day;
  const monday = new Date(
    Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() + diffToMonday)
  );
  const y = monday.getUTCFullYear();
  const m = monday.getUTCMonth();
  const dd = monday.getUTCDate();
  const key = `${y}-${pad2(m + 1)}-${pad2(dd)}`;
  const label = `${MONTH_LABELS[m]} ${dd}`;
  return { key, label };
};

export const ProficiencyBucketChart = ({
  rows,
  title = "Student Math Proficiency Distribution",
}) => {
  const [granularity, setGranularity] = useState("week");
  const LEVEL_DEFS = levelDefs();

  const data = useMemo(() => {
    if (!rows || rows.length === 0) return [];
    const bucketFn = granularity === "month" ? monthBucket : weekBucket;

    // For each (student, bucket), collect their classified proficiencies.
    // Null rows (sub-5-min / non-CONSENSUS sessions) are dropped so they can't
    // override valid ratings. A student is Unclassified for the bucket only if
    // they have zero classified sessions that bucket. The picked level is the
    // mode of their classified proficiencies; ties go to the lower level.
    const profsByStudentBucket = new Map();
    const studentsByBucket = new Map();
    const bucketLabels = new Map();
    rows.forEach((row) => {
      const sourceDate =
        granularity === "week"
          ? (row?.week_of || row?.session_date)
          : row?.session_date;
      const bucket = bucketFn(sourceDate);
      if (!bucket) return;
      const studentKey =
        row?.combined_student_id || row?.student_name || row?.unique_id;
      if (!studentKey) return;
      bucketLabels.set(bucket.key, bucket.label);
      if (!studentsByBucket.has(bucket.key)) {
        studentsByBucket.set(bucket.key, new Set());
      }
      studentsByBucket.get(bucket.key).add(studentKey);
      const level = row?.student_math_proficiency;
      if (level !== 1 && level !== 2 && level !== 3) return;
      const key = `${bucket.key}|${studentKey}`;
      if (!profsByStudentBucket.has(key)) profsByStudentBucket.set(key, []);
      profsByStudentBucket.get(key).push(level);
    });

    const pickLevel = (profs) => {
      const counts = { 1: 0, 2: 0, 3: 0 };
      profs.forEach((p) => {
        if (p === 1 || p === 2 || p === 3) counts[p] += 1;
      });
      let bestLevel = null;
      let bestCount = -1;
      for (const level of [1, 2, 3]) {
        if (counts[level] > bestCount) {
          bestCount = counts[level];
          bestLevel = level;
        }
      }
      return bestCount > 0 ? bestLevel : null;
    };

    const byBucket = new Map();
    studentsByBucket.forEach((students, bucketKey) => {
      const entry = {
        key: bucketKey,
        label: bucketLabels.get(bucketKey),
        counts: { 1: 0, 2: 0, 3: 0, unclassified: 0 },
        total: 0,
      };
      students.forEach((studentKey) => {
        entry.total += 1;
        const profs = profsByStudentBucket.get(`${bucketKey}|${studentKey}`) || [];
        const level = pickLevel(profs);
        if (level === 1 || level === 2 || level === 3) {
          entry.counts[level] += 1;
        } else {
          entry.counts.unclassified += 1;
        }
      });
      byBucket.set(bucketKey, entry);
    });

    return Array.from(byBucket.values())
      .map((e) => {
        const out = {
          key: e.key,
          bucket: e.label,
          total: e.total,
        };
        [1, 2, 3].forEach((level) => {
          out[`Level ${level}`] = e.counts[level];
        });
        out[UNCLASSIFIED_DEF.label] = e.counts.unclassified;
        const classifiedCount =
          out["Level 1"] + out["Level 2"] + out["Level 3"];
        const weighted =
          out["Level 1"] * 1 +
          out["Level 2"] * 2 +
          out["Level 3"] * 3;
        out.avg = classifiedCount
          ? Number((weighted / classifiedCount).toFixed(2))
          : 0;
        return out;
      })
      .sort((a, b) => (a.key < b.key ? -1 : a.key > b.key ? 1 : 0));
  }, [rows, granularity]);

  if (!rows || rows.length === 0) return null;

  const toggle = (
    <Toggle role="group" aria-label="Chart granularity">
      <ToggleButton
        type="button"
        $active={granularity === "week"}
        onClick={() => setGranularity("week")}
      >
        Week
      </ToggleButton>
      <ToggleButton
        type="button"
        $active={granularity === "month"}
        onClick={() => setGranularity("month")}
      >
        Month
      </ToggleButton>
    </Toggle>
  );

  if (data.length === 0) {
    return (
      <Wrapper>
        <Header>
          <Title>{title}</Title>
          {toggle}
        </Header>
        <Empty>No data points to chart.</Empty>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <Header>
        <Title>{title}</Title>
        {toggle}
      </Header>
      <ResponsiveContainer width="100%" height={420}>
        <ComposedChart data={data} margin={{ top: 24, right: 24, bottom: 8, left: 0 }}>
          <CartesianGrid stroke="#E5E7EB" strokeDasharray="3 3" />
          <XAxis dataKey="bucket" tick={{ fontSize: 12 }} />
          <YAxis
            yAxisId="left"
            allowDecimals={false}
            tick={{ fontSize: 12 }}
            label={{
              value: "Distinct Students",
              angle: -90,
              position: "insideLeft",
              style: { fontSize: 12, fill: "#6B7280" },
            }}
          />
          <YAxis
            yAxisId="right"
            orientation="right"
            domain={[1, 3]}
            tick={{ fontSize: 12 }}
          />
          <Tooltip
            formatter={(value, name) =>
              name === "Avg Proficiency (Right Axis)"
                ? [Number(value).toFixed(2), name]
                : [Number(value).toLocaleString(), name]
            }
          />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          {LEVEL_DEFS.map((def) => (
            <Bar
              key={def.level}
              yAxisId="left"
              dataKey={`Level ${def.level}`}
              name={def.label}
              stackId="levels"
              fill={def.color}
              isAnimationActive={false}
            />
          ))}
          <Bar
            yAxisId="left"
            dataKey={UNCLASSIFIED_DEF.label}
            name={UNCLASSIFIED_DEF.label}
            stackId="levels"
            fill={UNCLASSIFIED_DEF.color}
            isAnimationActive={false}
          />
          <Line
            yAxisId="right"
            type="monotone"
            dataKey="avg"
            name="Avg Proficiency (Right Axis)"
            stroke="#111111"
            strokeWidth={3}
            dot={{ r: 4, fill: "#111111" }}
            isAnimationActive={false}
          >
            <LabelList
              dataKey="avg"
              position="top"
              formatter={(v) => Number(v).toFixed(1)}
              style={{ fontSize: 12, fontWeight: 600, fill: "#111111" }}
            />
          </Line>
        </ComposedChart>
      </ResponsiveContainer>
    </Wrapper>
  );
};
